import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { HomlyLogo } from '../brand/HomlyLogo';
import AIBriefingHeader from '../ai/AIBriefingHeader';
import ReseauNotificationBell from '../reseau/ReseauNotificationBell';
import ThemeToggle from '../ui/ThemeToggle';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
    Bell,
    ExternalLink,
    LogOut,
    Menu,
    Settings,
    X,
} from 'lucide-react';

/**
 * ContextSwitcher — pill « Mon activité » ↔ « Mon réseau »
 * Présent partout dans le pro (Cockpit + Réseau) pour que le pro change
 * de contexte d'un clic, sans naviguer dans la sidebar.
 */
function ContextSwitcher({ user, isDark }) {
    const location = useLocation();
    if (!user) return null;
    const inReseau = location.pathname.startsWith('/reseau') || location.pathname.startsWith('/p/');
    const cockpitTarget = user.role === 'admin'
        ? '/admin'
        : user.role === 'agent' ? '/agent'
        : '/dashboard';

    // Styles thématisés selon le thème actif (dark pro vs light reseau)
    const railBase = isDark
        ? 'bg-white/[0.05] border border-white/10'
        : 'bg-[#f3eee7] border border-[#e7dcc9]';
    const activeBase = isDark
        ? 'bg-white text-[#1a0f2e] shadow-sm'
        : 'bg-white text-[#1a0f2e] shadow-sm';
    const inactiveBase = isDark
        ? 'text-white/70 hover:text-white'
        : 'text-[#5d5470] hover:text-[#1a0f2e]';

    return (
        <div
            className={`hidden md:inline-flex items-center gap-1 p-1 rounded-full ${railBase}`}
            data-testid="header-context-switcher"
        >
            <Link
                to={cockpitTarget}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${!inReseau ? activeBase : inactiveBase}`}
                data-testid="ctx-mon-activite"
            >
                Mon activité
            </Link>
            <Link
                to="/reseau/cockpit"
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${inReseau ? activeBase : inactiveBase}`}
                data-testid="ctx-mon-reseau"
            >
                Mon réseau
            </Link>
        </div>
    );
}

function HeaderBar({
    user,
    isDark,
    isAdmin,
    basePath,
    notifCounts,
    mobileMenuOpen,
    setMobileMenuOpen,
    onLogout,
}) {
    const navigate = useNavigate();

    return (
        <header
            className={`sticky top-0 z-40 backdrop-blur-xl border-b ${
                isDark ? 'bg-background/80 border-border' : 'bg-white/90 border-slate-200 shadow-sm'
            }`}
            style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
            <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4">
                {/* Mobile menu button */}
                <button
                    className={`md:hidden p-2 -ml-2 ${
                        isDark ? 'text-muted-foreground hover:text-white' : 'text-slate-600 hover:text-slate-900'
                    }`}
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    data-testid="mobile-menu-button"
                >
                    {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>

                {/* Mobile logo + compact briefing */}
                <div className="md:hidden flex items-center gap-2 min-w-0 flex-1 justify-center">
                    <HomlyLogo size="md" />
                    <AIBriefingHeader compact />
                </div>

                {/* Desktop: Briefing IA pill (replaces static "Bienvenue") */}
                <div className="hidden md:flex items-center gap-2 min-w-0 ml-2">
                    <AIBriefingHeader />
                </div>

                {/* Switcher Mon activité ↔ Mon réseau (centre, desktop) */}
                <div className="hidden md:flex flex-1 justify-center">
                    <ContextSwitcher user={user} isDark={isDark} />
                </div>

                {/* Right side actions */}
                <div className="flex items-center gap-2">
                    {/* View public profile */}
                    {isAdmin && (
                        <Link to={`/agence/${user?.agency_id || 'mon-agence'}`} target="_blank">
                            <Button
                                variant="ghost"
                                size="sm"
                                className={`hidden md:flex ${
                                    isDark
                                        ? 'text-muted-foreground hover:text-white hover:bg-card'
                                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                }`}
                            >
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Profil public
                            </Button>
                        </Link>
                    )}

                    {/* Bascule rapide thème sombre ⇄ clair */}
                    <ThemeToggle showLabels={false} size="sm" />

                    {/* Lot 1B.2 — Cloche notifications Réseau (dédiée, dropdown popover) */}
                    <ReseauNotificationBell basePath={basePath} />

                    {/* Notifications bell — badge prioritaire (rouge si urgent, orange si important) */}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`${basePath}/notifications`)}
                        className={`relative ${
                            isDark
                                ? 'text-muted-foreground hover:text-white hover:bg-card'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                        }`}
                        data-testid="notifications-button"
                        aria-label={`Notifications · ${notifCounts.total} non lue${notifCounts.total > 1 ? 's' : ''}`}
                    >
                        <Bell className="w-5 h-5" />
                        {notifCounts.urgent > 0 && (
                            <span
                                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
                                data-testid="notif-badge-urgent"
                            >
                                {notifCounts.urgent > 9 ? '9+' : notifCounts.urgent}
                            </span>
                        )}
                        {notifCounts.urgent === 0 && notifCounts.important > 0 && (
                            <span
                                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF914D] text-white text-[10px] font-bold flex items-center justify-center"
                                data-testid="notif-badge-important"
                            >
                                {notifCounts.important > 9 ? '9+' : notifCounts.important}
                            </span>
                        )}
                        {notifCounts.urgent === 0 && notifCounts.important === 0 && notifCounts.info > 0 && (
                            <span
                                className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"
                                data-testid="notif-badge-info"
                            />
                        )}
                    </Button>

                    {/* User menu */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className={isDark ? 'hover:bg-card' : 'hover:bg-slate-100'}
                                data-testid="user-menu-button"
                            >
                                <Avatar className="w-8 h-8 border border-purple-500/50">
                                    <AvatarFallback className="bg-purple-500 text-white text-xs font-bold">
                                        {user?.first_name?.[0]}
                                        {user?.last_name?.[0]}
                                    </AvatarFallback>
                                </Avatar>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className={`w-56 ${
                                isDark
                                    ? 'bg-slate-900 border-border text-white'
                                    : 'bg-white border-slate-200 text-slate-900'
                            }`}
                        >
                            <DropdownMenuLabel>
                                <div>
                                    <p className="font-medium">
                                        {user?.first_name} {user?.last_name}
                                    </p>
                                    <p className={`text-xs ${isDark ? 'text-muted-foreground' : 'text-slate-500'}`}>
                                        {user?.email}
                                    </p>
                                </div>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator className={isDark ? 'bg-muted' : 'bg-slate-200'} />
                            {isAdmin && (
                                <DropdownMenuItem
                                    onClick={() => navigate('/admin/settings')}
                                    className={
                                        isDark
                                            ? 'text-muted-foreground hover:text-white hover:bg-card focus:bg-card'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 focus:bg-slate-100'
                                    }
                                >
                                    <Settings className="w-4 h-4 mr-2" />
                                    Paramètres
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                onClick={onLogout}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 focus:bg-red-500/10"
                                data-testid="logout-button"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Déconnexion
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Mobile context switcher row (Mon activité ↔ Mon réseau) */}
            <MobileContextSwitcher user={user} isDark={isDark} />
        </header>
    );
}

function MobileContextSwitcher({ user, isDark }) {
    const location = useLocation();
    if (!user) return null;
    const inReseau = location.pathname.startsWith('/reseau') || location.pathname.startsWith('/p/');
    const cockpitTarget = user.role === 'admin'
        ? '/admin'
        : user.role === 'agent' ? '/agent'
        : '/dashboard';
    const railBase = isDark
        ? 'bg-white/[0.05] border border-white/10'
        : 'bg-[#f3eee7] border border-[#e7dcc9]';
    const activeBase = 'bg-white text-[#1a0f2e] shadow-sm';
    const inactiveBase = isDark
        ? 'text-white/70 hover:text-white'
        : 'text-[#5d5470] hover:text-[#1a0f2e]';
    return (
        <div className={`md:hidden px-3 pb-3 ${isDark ? 'bg-background/80' : 'bg-white/90'}`}>
            <div className={`inline-flex w-full items-center gap-1 p-1 rounded-full ${railBase}`} data-testid="mobile-context-switcher">
                <Link
                    to={cockpitTarget}
                    className={`flex-1 text-center px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${!inReseau ? activeBase : inactiveBase}`}
                    data-testid="mobile-ctx-mon-activite"
                >
                    Mon activité
                </Link>
                <Link
                    to="/reseau/cockpit"
                    className={`flex-1 text-center px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${inReseau ? activeBase : inactiveBase}`}
                    data-testid="mobile-ctx-mon-reseau"
                >
                    Mon réseau
                </Link>
            </div>
        </div>
    );
}

export default HeaderBar;
